import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Tabs,
    Tooltip,
    Typography,
} from "antd";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import ADSearchSelect, { EMPTY_VALUES } from "./ADSearchSelect";
import GroupList from "./GroupList";
import ResultPopup from "./ResultPopup";
import { createCombinedGroup } from "./accessGroupApi";
import { IDNOW_REQUEST_CENTER_URL } from "const";

const { Text, Title } = Typography;

/** IDNow request center link is sourced from const/index.tsx. */
const IdNowLink = () => (
    <p style={{ marginBottom: 0 }}>
        You can review your created groups in{" "}
        <a href={IDNOW_REQUEST_CENTER_URL} target="_blank" rel="noopener noreferrer">IDNow</a>, to be part of the group.
    </p>
);

/** Explains the governance group and how it is created. */
const GOVERNANCE_GROUP_NOTE = (
    <div>
        <p style={{ marginBottom: 8 }}>
            A <strong>Governance Group</strong> manages who belongs to the access group. The
            group owner you choose owns the group, and any members you add become its members.
        </p>
        <p style={{ marginBottom: 8 }}>
            It is created first, automatically, after a successful submission and may take
            several hours to become active.
        </p>
    </div>
);

/** Explains the entitlement group and how it is created. */
const ENTITLEMENT_GROUP_NOTE = (
    <div>
        <p style={{ marginBottom: 8 }}>
            An <strong>Entitlement Group</strong> grants access and is what gets attached to your
            space. It uses the governance group as its approver, and the owner you choose becomes
            the group owner.
        </p>
        <p style={{ marginBottom: 8 }}>
            It is created after the governance group, automatically, and may take several hours to
            become active.
        </p>
        <IdNowLink />
    </div>
);

/** White tooltip styling with dark text for the section info notes. */
const NOTE_TOOLTIP_COLOR = "#fff";
const NOTE_OVERLAY_INNER_STYLE: React.CSSProperties = { maxWidth: 380, color: "rgba(0, 0, 0, 0.85)" };

const GROUP_PREFIX = "PH-RND-DSE-";

/** Backend caps the full entitlement name (prefix included) at 58 characters. */
const ENT_NAME_MAX = 58;
const ENT_SUFFIX_MAX = ENT_NAME_MAX - GROUP_PREFIX.length;

/** Backend requires at least two governance members for the approval workflow. */
const MIN_GOV_MEMBERS = 2;

/** Validates group name suffix: alphanumeric + hyphens, no leading/trailing hyphens. */
const isValidGroupNameSuffix = (suffix: string): boolean => {
    if (!suffix || suffix.trim().length === 0) return false;
    return /^[A-Za-z0-9]+([A-Za-z0-9-]*[A-Za-z0-9]+)?$/.test(suffix.trim());
};

interface ICombinedFormValues {
    /** Governance group owner (single email). */
    govOwner: string;
    /** Governance group name suffix (prefix is enforced). */
    govNameSuffix: string;
    /** Governance group members. */
    govMembers: string[];
    /** Governance group description. */
    govDescription: string;
    /** Entitlement group owner (single email — IAM supports one owner only). */
    entOwner: string;
    /** Entitlement group name suffix (prefix is enforced). */
    entNameSuffix: string;
    /** Entitlement group description. */
    entDescription: string;
}

const groupNameSuffixRules = [
    { required: true, message: "Group name suffix is required." },
    {
        validator: (_: unknown, value: string) =>
            !value || isValidGroupNameSuffix(value)
                ? Promise.resolve()
                : Promise.reject(
                    new Error(
                        "Only letters, digits, and hyphens are allowed. Cannot start or end with a hyphen.",
                    ),
                ),
    },
];

const entNameSuffixRules = [
    ...groupNameSuffixRules,
    {
        max: ENT_SUFFIX_MAX,
        message: `Group name suffix cannot exceed ${ENT_SUFFIX_MAX} characters.`,
    },
];

/** Requires at least two distinct non-empty governance members before submit. */
const govMembersListRules = [
    {
        validator: async (_: unknown, members: string[]) => {
            const distinct = new Set(
                (members ?? [])
                    .filter((m) => m?.trim())
                    .map((m) => m.trim().toLowerCase()),
            );
            if (distinct.size < MIN_GOV_MEMBERS) {
                return Promise.reject(
                    new Error(`At least ${MIN_GOV_MEMBERS} distinct members are required.`),
                );
            }
            return Promise.resolve();
        },
    },
];

const descriptionRules = [
    { required: true, message: "Description is required." },
    { whitespace: true, message: "Description is required." },
    { min: 10, message: "Description must be at least 10 characters." },
];

const groupNameLabel = (
    <span>
        <Text strong>Group Name</Text>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            (Prefix <strong>{GROUP_PREFIX}</strong> enforced)
        </Text>
    </span>
);

/**
 * Single-page form that creates a governance group and its entitlement in one
 * submission. The governance section is shown first, the entitlement section
 * second, and a single "Create Group" button submits the combined payload.
 */
const CombinedGroupForm: React.FC = () => {
    const [form] = Form.useForm<ICombinedFormValues>();
    const msalInstance = useMsal();

    const [submitting, setSubmitting] = useState(false);
    // Errors stay hidden until the first submit attempt; afterwards fields
    // validate live so the user can see them clear as they fix each one.
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Result banner shown after a submit attempt completes.
    const [submitResult, setSubmitResult] = useState<
        { status: "success" | "error"; message: string } | null
    >(null);
    // Bumped after each successful create to refresh the lists below.
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState<"create" | "groups">("create");
    const [groupCount, setGroupCount] = useState<number | null>(null);

    const watchedGovOwner = Form.useWatch("govOwner", form) as string | undefined;

    // Hide the owner and every other already-picked member from each row's
    // dropdown so a duplicate can never be selected in the first place.
    const watchedGovMembers = Form.useWatch("govMembers", form) as (string | undefined)[] | undefined;
    const memberExclusionFor = useMemo(() => {
        const picked = watchedGovMembers ?? [];
        return (index: number) => {
            const others = picked.filter((m, i): m is string => i !== index && !!m?.trim());
            if (!watchedGovOwner && others.length === 0) return EMPTY_VALUES;
            return watchedGovOwner ? [watchedGovOwner, ...others] : others;
        };
    }, [watchedGovMembers, watchedGovOwner]);

    // The entitlement is always approved by the governance group being created,
    // so the approver group mirrors the governance group name entered above.
    const watchedGovNameSuffix = Form.useWatch("govNameSuffix", form) as string | undefined;
    const governanceGroupName = watchedGovNameSuffix?.trim()
        ? `${GROUP_PREFIX}${watchedGovNameSuffix.trim()}`
        : "";

    // Once the user has attempted a submit, keep the duplicate-owner check on
    // governance member rows fresh whenever the governance owner changes.
    const handleValuesChange = (changed: Partial<ICombinedFormValues>) => {
        if (hasSubmitted && "govOwner" in changed) {
            const members = (form.getFieldValue("govMembers") as string[]) ?? [];
            members.forEach((_, idx) => {
                form.validateFields([["govMembers", idx]]).catch(() => undefined);
            });
        }
    };

    const handleFinish = async (values: ICombinedFormValues) => {
        const governanceName = `${GROUP_PREFIX}${values.govNameSuffix.trim()}`;
        const entitlementName = `${GROUP_PREFIX}${values.entNameSuffix.trim()}`;
        // Dedupe case-insensitively so repeated picks don't reach the backend twice.
        const seenMembers = new Set<string>();
        const members = (values.govMembers ?? [])
            .map((m) => m?.trim() ?? "")
            .filter((m) => {
                if (!m) return false;
                const key = m.toLowerCase();
                if (seenMembers.has(key)) return false;
                seenMembers.add(key);
                return true;
            });

        setSubmitting(true);
        setSubmitResult(null);
        try {
            await createCombinedGroup(msalInstance, {
                governance_name: governanceName,
                governance_description: values.govDescription.trim(),
                governance_owner: values.govOwner,
                governance_member: members,
                entitlement_name: entitlementName,
                entitlement_description: values.entDescription.trim(),
                entitlement_owner: values.entOwner,
                entitlementAdditionalOwners: [],
                entitlementAllowAccessRequests: true,
                entitlementGroupType: "Security",
            });
            setSubmitResult({
                status: "success",
                message: `Group creation for "${governanceName}" and "${entitlementName}" has been triggered successfully. It may take several hours to become active.`,
            });
            // Refresh the list tab and switch to it so the user sees their new group.
            setListRefreshKey((k) => k + 1);
            setActiveTab("groups");
            form.resetFields();
            setHasSubmitted(false);
        } catch (error) {
            type ValidationError = { msg: string; loc?: string[] };
            type ErrorData = { message?: string; detail?: string | ValidationError[] };
            const data = (error as { response?: { data?: ErrorData } })?.response?.data;
            let reason: string;
            if (data?.message) {
                reason = data.message;
            } else if (data && Array.isArray(data.detail)) {
                reason = data.detail.map((e: ValidationError) => e.msg).join("; ");
            } else if (typeof data?.detail === "string") {
                reason = data.detail;
            } else {
                reason = (error as Error)?.message ?? "An unexpected error occurred. Please try again.";
            }
            setSubmitResult({
                status: "error",
                message: `Failed to create group: ${reason}`,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const tabItems = [
        {
            key: "create",
            label: "Create Group",
            children: (
                <div style={{ width: "100%", background: "#fff", padding: 24, borderRadius: 8 }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                onFinishFailed={() => setHasSubmitted(true)}
                onValuesChange={handleValuesChange}
                validateTrigger={hasSubmitted ? "onChange" : []}
                requiredMark={false}
                initialValues={{ govMembers: ["", ""] }}
            >
                {/* ---------------------------------------------------------- */}
                {/* Governance group section                                   */}
                {/* ---------------------------------------------------------- */}
                <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                    Governance Group
                    <Tooltip title={GOVERNANCE_GROUP_NOTE} color={NOTE_TOOLTIP_COLOR} overlayInnerStyle={NOTE_OVERLAY_INNER_STYLE}>
                        <InfoCircleOutlined style={{ color: "#1890ff", fontSize: 15, marginLeft: 8 }} />
                    </Tooltip>
                </Title>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            label={<Text strong>Group Owner</Text>}
                            name="govOwner"
                            rules={[
                                { required: true, message: "Group owner is required." },
                                { type: "email", message: "Enter a valid email address." },
                            ]}
                        >
                            <ADSearchSelect mode="user" placeholder="owner@example.com" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label={groupNameLabel} name="govNameSuffix" rules={groupNameSuffixRules}>
                            <Input addonBefore={GROUP_PREFIX} placeholder="my-group-name" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Text strong>Members</Text>
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                            (minimum {MIN_GOV_MEMBERS})
                        </Text>
                        <Form.List name="govMembers" rules={govMembersListRules}>
                            {(fields, { add, remove }, { errors }) => (
                                <div style={{ marginTop: 8 }}>
                                    {fields.map((field) => (
                                        <div key={field.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Form.Item
                                                    name={field.name}
                                                    rules={[
                                                        { required: true, message: "Member email is required." },
                                                        { type: "email", message: "Enter a valid email address." },
                                                        {
                                                            validator: (_, value) =>
                                                                value && value === form.getFieldValue("govOwner")
                                                                    ? Promise.reject(
                                                                        new Error("Member cannot be the same as the Group Owner."),
                                                                    )
                                                                    : Promise.resolve(),
                                                        },
                                                    ]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <ADSearchSelect
                                                        mode="user"
                                                        placeholder="member@example.com"
                                                        excludeValues={memberExclusionFor(field.name)}
                                                    />
                                                </Form.Item>
                                            </div>
                                            {fields.length > MIN_GOV_MEMBERS && (
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(field.name)}
                                                />
                                            )}
                                        </div>
                                    ))}
                                    <Form.ErrorList errors={errors} />
                                    <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        onClick={() => add("")}
                                        style={{ marginTop: 4 }}
                                    >
                                        Add Member
                                    </Button>
                                </div>
                            )}
                        </Form.List>
                    </Col>

                    <Col span={12}>
                        <Form.Item label={<Text strong>Description</Text>} name="govDescription" rules={descriptionRules}>
                            <Input.TextArea
                                rows={3}
                                maxLength={500}
                                showCount
                                placeholder="Describe the purpose of this group (min. 10 characters)"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                {/* ---------------------------------------------------------- */}
                {/* Entitlement group section                                  */}
                {/* ---------------------------------------------------------- */}
                <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                    Entitlement Group
                    <Tooltip title={ENTITLEMENT_GROUP_NOTE} color={NOTE_TOOLTIP_COLOR} overlayInnerStyle={NOTE_OVERLAY_INNER_STYLE}>
                        <InfoCircleOutlined style={{ color: "#1890ff", fontSize: 15, marginLeft: 8 }} />
                    </Tooltip>
                </Title>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            label={<Text strong>Group Owner</Text>}
                            name="entOwner"
                            rules={[
                                { required: true, message: "Owner email is required." },
                                { type: "email", message: "Enter a valid email address." },
                            ]}
                        >
                            <ADSearchSelect mode="user" placeholder="owner@example.com" />
                        </Form.Item>

                        <div style={{ marginTop: 16 }}>
                            <Text strong>Governance Approver Group</Text>
                            <Input
                                value={governanceGroupName}
                                readOnly
                                placeholder="Governance Group name will be mirrored here"
                                style={{ marginTop: 8, cursor: "not-allowed" }}
                            />
                        </div>
                    </Col>

                    <Col span={12}>
                        <Form.Item label={groupNameLabel} name="entNameSuffix" rules={entNameSuffixRules}>
                            <Input addonBefore={GROUP_PREFIX} maxLength={ENT_SUFFIX_MAX} placeholder="my-group-name" />
                        </Form.Item>
                        <Form.Item label={<Text strong>Description</Text>} name="entDescription" rules={descriptionRules}>
                            <Input.TextArea
                                rows={3}
                                maxLength={500}
                                showCount
                                placeholder="Describe the purpose of this group (min. 10 characters)"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Create Group
                    </Button>
                </Form.Item>
            </Form>
                </div>
            ),
        },
        {
            key: "groups",
            label: (
                <Badge count={groupCount ?? 0} size="small" offset={[6, -2]} style={{ backgroundColor: "#1890ff" }}>
                    My Groups
                </Badge>
            ),
            children: <GroupList refreshKey={listRefreshKey} onCountChange={setGroupCount} />,
        },
    ];

    return (
        <>
            {/* Portal-based modal: renders at document.body regardless of active tab */}
            {submitResult && (
                <ResultPopup
                    open={!!submitResult}
                    status={submitResult.status}
                    message={submitResult.message}
                    onClose={() => setSubmitResult(null)}
                />
            )}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as "create" | "groups")}
                items={tabItems}
                style={{ marginTop: 8 }}
            />
        </>
    );
};

export default CombinedGroupForm;
